import type { Settlement, ShipmentBox } from 'common/src/utils/types';
import {
  buildSettlementQuadratureProductKey,
  buildShipmentQuadratureProductKey,
} from '../../../common/src/utils/quadrature';

// Export full precision. Excel number formats round only the displayed values,
// so pivot tables and sums do not lose fractions on each allocated row.
const normalizeReferenceToken = (value: string | number | null | undefined) =>
  (String(value ?? '').trim() || 'SIN REFERENCIA').toUpperCase();

type LiquidatedDetailAggregate = {
  kilos: number;
  quantity: number;
  localTotalPrice: number;
  localTotalCommission: number;
  localTotalTax: number;
  localOtherCosts: number;
  localRepackingCosts: number;
  localCif: number;
  localFreight: number;
  localFob: number;
  saleUsd: number;
  commissionUsd: number;
  taxUsd: number;
  otherCostsUsd: number;
  repackingUsd: number;
  cifUsd: number;
  freightUsd: number;
  fobUsd: number;
  extraRevenueLocal: number;
  extraRevenueUsd: number;
  adjustedSaleLocal: number;
  adjustedCifLocal: number;
  adjustedFobLocal: number;
  adjustedSaleUsd: number;
  adjustedCifUsd: number;
  adjustedFobUsd: number;
  exchangeRateWeightedSum: number;
  exchangeRateWeight: number;
  currencies: Set<string>;
};

type LiquidatedDetailIndex = {
  references: Set<string>;
  detailsByReferenceAndProduct: Map<string, LiquidatedDetailAggregate>;
};

const createEmptyLiquidatedDetailAggregate = (): LiquidatedDetailAggregate => ({
  kilos: 0,
  quantity: 0,
  localTotalPrice: 0,
  localTotalCommission: 0,
  localTotalTax: 0,
  localOtherCosts: 0,
  localRepackingCosts: 0,
  localCif: 0,
  localFreight: 0,
  localFob: 0,
  saleUsd: 0,
  commissionUsd: 0,
  taxUsd: 0,
  otherCostsUsd: 0,
  repackingUsd: 0,
  cifUsd: 0,
  freightUsd: 0,
  fobUsd: 0,
  extraRevenueLocal: 0,
  extraRevenueUsd: 0,
  adjustedSaleLocal: 0,
  adjustedCifLocal: 0,
  adjustedFobLocal: 0,
  adjustedSaleUsd: 0,
  adjustedCifUsd: 0,
  adjustedFobUsd: 0,
  exchangeRateWeightedSum: 0,
  exchangeRateWeight: 0,
  currencies: new Set<string>(),
});

const toNumber = (value: number | string | null | undefined) => {
  const numericValue = Number(value || 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const buildLiquidatedDetailIndex = (
  settlements: Settlement[]
): LiquidatedDetailIndex => {
  const references = new Set<string>();
  const detailsByReferenceAndProduct = new Map<
    string,
    LiquidatedDetailAggregate
  >();

  settlements.forEach((settlement) => {
    const reference = normalizeReferenceToken(settlement.reference_number);
    if (!reference) return;
    const quadratureContext = {
      season: settlement.season,
      speciesId: settlement.species_id,
    };
    references.add(reference);

    settlement.settlement_details?.forEach((detail) => {
      const productKey = buildSettlementQuadratureProductKey(
        detail,
        quadratureContext
      );
      const key = `${reference}::${productKey}`;
      const current =
        detailsByReferenceAndProduct.get(key) ||
        createEmptyLiquidatedDetailAggregate();

      const exchangeRate = toNumber(
        detail.exchange_rate || settlement.exchange_rate || 1
      );
      const safeExchangeRate = exchangeRate || 1;
      const kilos = toNumber(detail.kilos);
      const quantity = toNumber(detail.quantity);
      const localTotalPrice = toNumber(detail.local_total_price);
      const localTotalCommission = toNumber(detail.local_total_commision);
      const localTotalTax = toNumber(detail.local_total_tax);
      const localOtherCosts = toNumber(detail.local_other_costs);
      const localRepackingCosts = toNumber(detail.local_repacking_costs);
      const freightUsd = toNumber(detail.usd_freight);
      const fobUsd = toNumber(detail.usd_total_fob);
      const localCif =
        localTotalPrice -
        localTotalTax -
        localTotalCommission -
        localOtherCosts -
        localRepackingCosts;
      const extraRevenueLocal = (
        detail.settlement_detail_revenues || []
      ).reduce((sum, revenue) => sum + toNumber(revenue.amount_local), 0);
      const extraRevenueUsd = (detail.settlement_detail_revenues || []).reduce(
        (sum, revenue) => sum + toNumber(revenue.amount_usd),
        0
      );

      current.kilos += kilos;
      current.quantity += quantity;
      current.localTotalPrice += localTotalPrice;
      current.localTotalCommission += localTotalCommission;
      current.localTotalTax += localTotalTax;
      current.localOtherCosts += localOtherCosts;
      current.localRepackingCosts += localRepackingCosts;
      current.localCif += localCif;
      current.localFreight += freightUsd * safeExchangeRate;
      current.localFob += fobUsd * safeExchangeRate;
      current.saleUsd += localTotalPrice / safeExchangeRate;
      current.commissionUsd += localTotalCommission / safeExchangeRate;
      current.taxUsd += localTotalTax / safeExchangeRate;
      current.otherCostsUsd += localOtherCosts / safeExchangeRate;
      current.repackingUsd += localRepackingCosts / safeExchangeRate;
      current.cifUsd += localCif / safeExchangeRate;
      current.freightUsd += freightUsd;
      current.fobUsd += fobUsd;
      current.extraRevenueLocal += extraRevenueLocal;
      current.extraRevenueUsd += extraRevenueUsd;
      current.adjustedSaleLocal += localTotalPrice + extraRevenueLocal;
      current.adjustedCifLocal += localCif + extraRevenueLocal;
      current.adjustedFobLocal += (fobUsd + extraRevenueUsd) * safeExchangeRate;
      current.adjustedSaleUsd +=
        localTotalPrice / safeExchangeRate + extraRevenueUsd;
      current.adjustedCifUsd += localCif / safeExchangeRate + extraRevenueUsd;
      current.adjustedFobUsd += fobUsd + extraRevenueUsd;
      current.exchangeRateWeightedSum += safeExchangeRate * kilos;
      current.exchangeRateWeight += kilos;
      if (detail.local_currency) current.currencies.add(detail.local_currency);

      detailsByReferenceAndProduct.set(key, current);
    });
  });

  return { references, detailsByReferenceAndProduct };
};

export const buildLiquidatedDetailColumns = (
  box: ShipmentBox,
  liquidatedDetailIndex: LiquidatedDetailIndex
) => {
  const reference = normalizeReferenceToken(box.reference_number);
  const productKey = buildShipmentQuadratureProductKey(box, {
    season: box.season,
    speciesId: box.species_id,
  });
  const aggregate = liquidatedDetailIndex.detailsByReferenceAndProduct.get(
    `${reference}::${productKey}`
  );

  const emptyColumns = (status: string) => ({
    'Estado Liquidado Detalle': status,
    'Moneda Local Liquidación': '',
    'Tipo de Cambio Liquidación': null,
    'Venta Moneda Local Liquidada': null,
    'Comisión Moneda Local Liquidada': null,
    'Impuestos Moneda Local Liquidada': null,
    'Otros Costos Moneda Local Liquidada': null,
    'Repacking Moneda Local Liquidado': null,
    'CIF Moneda Local Liquidado': null,
    'Flete Moneda Local Liquidado': null,
    'FOB Moneda Local Liquidado': null,
    'Venta USD Liquidada': null,
    'Comisión USD Liquidada': null,
    'Impuestos USD Liquidados': null,
    'Otros Costos USD Liquidados': null,
    'Repacking USD Liquidado': null,
    'CIF USD Liquidado': null,
    'Flete USD Liquidado': null,
    'FOB USD Liquidado': null,
    'Otros Ingresos Moneda Local Liquidado': null,
    'Otros Ingresos USD Liquidado': null,
    'Venta Ajustada Moneda Local Liquidada': null,
    'CIF Ajustado Moneda Local Liquidado': null,
    'FOB Ajustado Moneda Local Liquidado': null,
    'Venta Ajustada USD Liquidada': null,
    'CIF Ajustado USD Liquidado': null,
    'FOB Ajustado USD Liquidado': null,
    'Venta USD/kg Liquidada': null,
    'Costos USD/kg Liquidado': null,
    'FOB USD/kg Liquidado': null,
    'FOB Ajustado USD/kg Liquidado': null,
  });

  if (!liquidatedDetailIndex.references.has(reference)) {
    return emptyColumns('Sin liquidación');
  }

  if (!aggregate) {
    return emptyColumns('Sin match producto');
  }

  if (aggregate.kilos <= 0) {
    return emptyColumns('Sin kilos liquidación');
  }

  const rowKilos =
    toNumber(box.box_weight ?? box.unit_weight) * toNumber(box.quantity);
  const kgFactor = rowKilos / aggregate.kilos;
  const rowSaleLocal = aggregate.localTotalPrice * kgFactor;
  const rowSaleUsd = aggregate.saleUsd * kgFactor;
  const localSalesFactor =
    aggregate.localTotalPrice !== 0
      ? rowSaleLocal / aggregate.localTotalPrice
      : kgFactor;
  const usdSalesFactor =
    aggregate.saleUsd !== 0 ? rowSaleUsd / aggregate.saleUsd : kgFactor;

  const rowCommissionLocal = aggregate.localTotalCommission * localSalesFactor;
  const rowTaxLocal = aggregate.localTotalTax * localSalesFactor;
  const rowExtraRevenueLocal = aggregate.extraRevenueLocal * localSalesFactor;
  const rowOtherCostsLocal = aggregate.localOtherCosts * kgFactor;
  const rowRepackingLocal = aggregate.localRepackingCosts * kgFactor;
  const rowFreightLocal = aggregate.localFreight * kgFactor;
  const rowCifLocal = aggregate.localCif * kgFactor;
  const rowFobLocal = aggregate.localFob * kgFactor;

  const rowCommissionUsd = aggregate.commissionUsd * usdSalesFactor;
  const rowTaxUsd = aggregate.taxUsd * usdSalesFactor;
  const rowExtraRevenueUsd = aggregate.extraRevenueUsd * usdSalesFactor;
  const rowOtherCostsUsd = aggregate.otherCostsUsd * kgFactor;
  const rowRepackingUsd = aggregate.repackingUsd * kgFactor;
  const rowFreightUsd = aggregate.freightUsd * kgFactor;
  const rowCifUsd = aggregate.cifUsd * kgFactor;
  // The stored FOB includes repacking and is the settlement export source.
  const rowFobUsd = aggregate.fobUsd * kgFactor;
  const rowTotalCostsUsd =
    rowCommissionUsd +
    rowTaxUsd +
    rowOtherCostsUsd +
    rowRepackingUsd +
    rowFreightUsd;
  const perRowKg = (value: number) => (rowKilos > 0 ? value / rowKilos : null);

  return {
    'Estado Liquidado Detalle': 'Liquidado',
    'Moneda Local Liquidación': Array.from(aggregate.currencies).join(', '),
    'Tipo de Cambio Liquidación':
      aggregate.exchangeRateWeight > 0
        ? aggregate.exchangeRateWeightedSum / aggregate.exchangeRateWeight
        : null,
    'Venta Moneda Local Liquidada': rowSaleLocal,
    'Comisión Moneda Local Liquidada': rowCommissionLocal,
    'Impuestos Moneda Local Liquidada': rowTaxLocal,
    'Otros Costos Moneda Local Liquidada': rowOtherCostsLocal,
    'Repacking Moneda Local Liquidado': rowRepackingLocal,
    'CIF Moneda Local Liquidado': rowCifLocal,
    'Flete Moneda Local Liquidado': rowFreightLocal,
    'FOB Moneda Local Liquidado': rowFobLocal,
    'Venta USD Liquidada': rowSaleUsd,
    'Comisión USD Liquidada': rowCommissionUsd,
    'Impuestos USD Liquidados': rowTaxUsd,
    'Otros Costos USD Liquidados': rowOtherCostsUsd,
    'Repacking USD Liquidado': rowRepackingUsd,
    'CIF USD Liquidado': rowCifUsd,
    'Flete USD Liquidado': rowFreightUsd,
    'FOB USD Liquidado': rowFobUsd,
    'Otros Ingresos Moneda Local Liquidado': rowExtraRevenueLocal,
    'Otros Ingresos USD Liquidado': rowExtraRevenueUsd,
    'Venta Ajustada Moneda Local Liquidada':
      rowSaleLocal + rowExtraRevenueLocal,
    'CIF Ajustado Moneda Local Liquidado': rowCifLocal + rowExtraRevenueLocal,
    'FOB Ajustado Moneda Local Liquidado': rowFobLocal + rowExtraRevenueLocal,
    'Venta Ajustada USD Liquidada': rowSaleUsd + rowExtraRevenueUsd,
    'CIF Ajustado USD Liquidado': rowCifUsd + rowExtraRevenueUsd,
    'FOB Ajustado USD Liquidado': rowFobUsd + rowExtraRevenueUsd,
    'Venta USD/kg Liquidada': perRowKg(rowSaleUsd),
    'Costos USD/kg Liquidado': perRowKg(rowTotalCostsUsd),
    'FOB USD/kg Liquidado': perRowKg(rowFobUsd),
    'FOB Ajustado USD/kg Liquidado': perRowKg(rowFobUsd + rowExtraRevenueUsd),
  };
};
